import type { AxiosRequestConfig } from "axios";
import { reject } from "lodash";
import type { Emitter } from "mitt";
import mitt from "mitt";
import { defineStore } from "pinia";
import type { Ref, UnwrapNestedRefs } from "vue";
import { reactive, ref } from "vue";
import type { ConfigurationAdditionResponse } from "../../responses/ConfigurationAdditionResponse.ts";
import type { ConfigurationCategoryResponse } from "../../responses/ConfigurationCategoryResponse.ts";
import type { ConfigurationOptionResponse } from "../../responses/ConfigurationOptionResponse.ts";
import type { ConfigurationResponse } from "../../responses/ConfigurationResponse.ts";
import type { ProductResponse } from "../../responses/ProductResponse.ts";
import { api } from "../scripts/api.ts";

export interface SelectedAddition {
    category_id: number;
    addition_id: number;
    option_id: number | null;
    option: ConfigurationOptionResponse | null;
}

type Events = {
    reset: void;
    initialized: void;
};

export const useConfigurationStore = defineStore("configuration", () => {
    const configuration: Ref<ConfigurationResponse | null> = ref(null);
    const product: Ref<ProductResponse | null> = ref(null);
    const selectedAdditions: Ref<Record<number, SelectedAddition>> = ref({});
    const calculatePriceRequest: UnwrapNestedRefs<{
        abort: AbortController | null;
    }> = reactive({ abort: null });
    const getConfigurationRequest: UnwrapNestedRefs<{
        abort: AbortController | null;
    }> = reactive({ abort: null });
    const validateRequest: UnwrapNestedRefs<{ abort: AbortController | null }> =
        reactive({ abort: null });
    const calculatedPriceIncl: Ref<number> = ref(0);
    const calculatedPriceExcl: Ref<number> = ref(0);
    const priceIncl: Ref<boolean> = ref(true);
    const loading: Ref<boolean> = ref(false);
    const events: Emitter<Events> = mitt<Events>();
    const validationErrors: Ref<string[]> = ref([]);

    const setConfiguration = (config: ConfigurationResponse | null) => {
        configuration.value = config;
    };

    const setProduct = (prod: ProductResponse) => {
        product.value = prod;
        calculatedPriceExcl.value = product.value.price?.price_excl;
        calculatedPriceIncl.value = product.value.price?.price_incl;
    };

    const selectAddition = (
        category: ConfigurationCategoryResponse,
        addition: ConfigurationAdditionResponse,
        optionId: number | null = null,
        calculate: boolean = false,
    ) => {
        if (category.type === "single_option") {
            removeSelectedAdditions(category.id);
        }

        selectedAdditions.value[addition.id] = {
            category_id: category.id,
            addition_id: addition.id,
            option_id: optionId,
            option: null,
        };

        if (calculate) {
            calculatePrice();
        }
    };

    const removeSelectedAdditions = (categoryId: number) => {
        for (const [additionId, addition] of Object.entries(
            selectedAdditions.value,
        )) {
            if (addition.category_id === categoryId) {
                delete selectedAdditions.value[additionId];
            }
        }
    };

    const toggleAddition = (
        category: ConfigurationCategoryResponse,
        addition: ConfigurationAdditionResponse,
    ) => {
        const selectedAddition = selectedAdditions.value[addition.id];
        if (!selectedAddition) {
            if (addition.options.length > 0) {
                selectAddition(category, addition, addition.options[0].id);
            } else {
                selectAddition(category, addition);
            }
        } else {
            removeAddition(addition.id);
        }

        calculatePrice();
    };

    const removeAddition = (additionId: number) => {
        delete selectedAdditions.value[additionId];
    };

    const hasAddition = (additionId: number) => {
        return !!selectedAdditions.value[additionId];
    };

    const selectOption = (additionId: number, optionId: number) => {
        const addition = selectedAdditions.value[additionId];

        if (!addition) {
            throw new Error(`Addition ${additionId} has not been selected`);
        }

        addition.option_id = optionId;
    };

    const calculatePrice = (): Promise<number> => {
        if (calculatePriceRequest.abort !== null) {
            calculatePriceRequest.abort.abort();
        }
        calculatePriceRequest.abort = new AbortController();
        loading.value = true;
        validationErrors.value = [];

        return api()
            .post(
                "/site/configuration/calculate/",
                {
                    configuration_id: configuration.value.id,
                    product_id: product.value.id,
                    additions: selectedAdditions.value,
                    price_incl: priceIncl.value,
                },
                {
                    signal: calculatePriceRequest.abort.signal,
                } as AxiosRequestConfig,
            )
            .then((response) => {
                calculatedPriceIncl.value = response.data.price_incl;
                calculatedPriceExcl.value = response.data.price_excl;

                loading.value = false;
            });
    };

    const editConfiguration = (cart_line_id: number, cart_uuid: string) => {
        if (getConfigurationRequest.abort !== null) {
            getConfigurationRequest.abort.abort();
        }
        getConfigurationRequest.abort = new AbortController();

        loading.value = true;
        try {
            return api()
                .post(
                    "/site/configuration/request-edit/",
                    {
                        cart_line_id: cart_line_id,
                        cart: cart_uuid,
                    },
                    {
                        signal: getConfigurationRequest.abort.signal,
                    } as AxiosRequestConfig,
                )
                .then((response) => {
                    setConfiguration(response.data.configuration);
                    setProduct(response.data.product);

                    selectedAdditions.value = response.data.additions;
                    calculatePrice().then(() => {
                        loading.value = false;
                    });
                });
        } catch (e) {
            reject(e);
        }
    };

    const updateConfiguration = (cart_line_id: number, cart_uuid: string) => {
        if (getConfigurationRequest.abort !== null) {
            getConfigurationRequest.abort.abort();
        }
        getConfigurationRequest.abort = new AbortController();

        loading.value = true;
        try {
            return api()
                .post(
                    "/site/configuration/update/",
                    {
                        cart_line_id: cart_line_id,
                        cart: cart_uuid,
                        configuration: selectedAdditions.value,
                    },
                    {
                        signal: getConfigurationRequest.abort.signal,
                    } as AxiosRequestConfig,
                )
                .then((response) => {
                    return response;
                });
        } catch (e) {
            reject(e);
        }
    };

    const validate = () => {
        if (validateRequest.abort !== null) {
            validateRequest.abort.abort();
        }
        validateRequest.abort = new AbortController();

        loading.value = true;
        validationErrors.value = [];
        try {
            return api()
                .post(
                    "/site/configuration/validate/",
                    {
                        additions: selectedAdditions.value,
                        configuration_id: configuration.value?.id,
                    },
                    {
                        signal: validateRequest.abort.signal,
                    } as AxiosRequestConfig,
                )
                .then((response) => {
                    validationErrors.value = response.data;
                })
                .catch((error) => {
                    validationErrors.value = error.response.data.errors;
                })
                .finally(() => {
                    loading.value = false;
                });
        } catch (e) {
            reject(e);
        }
    };

    const reset = () => {
        selectedAdditions.value = {};
        events.emit("reset");
    };

    return {
        configuration,
        product,
        selectedAdditions,
        calculatedPriceIncl,
        calculatedPriceExcl,
        priceIncl,
        loading,
        events,
        validationErrors,
        validate,
        reset,
        setConfiguration,
        setProduct,
        selectOption,
        selectAddition,
        toggleAddition,
        hasAddition,
        removeAddition,
        calculatePrice,
        editConfiguration,
        updateConfiguration,
    };
});
